import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  recruiterKey,
  managerKey,
} from "../../../helpers/DashboardPipelineHelper";
import HelpTooltip from "../../common/HelpTooltip";

export default function DashboardTables({
  dashboardCounts,
  recruiterFilter,
  managerFilter,
  onRecruiterFilterChange,
  onManagerFilterChange,
}) {
  const navigate = useNavigate();
  const rows = Array.isArray(dashboardCounts) ? dashboardCounts : [];

  const drillLink = (path, title) => (value) => (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none fw-semibold"
      title={title}
      onClick={() => navigate(path)}
    >
      {value ?? 0}
    </button>
  );

  const managerOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const value = managerKey(r);
      if (!value) return;
      const label = String(r.manager_name ?? "").trim() || value;
      if (!map.has(value)) map.set(value, label);
    });
    return [...map.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: "base" }))
      .map(([value, label]) => ({ value, label }));
  }, [rows]);

  const rowsForRecruiterList = useMemo(() => {
    if (!managerFilter) return rows;
    return rows.filter((r) => managerKey(r) === managerFilter);
  }, [rows, managerFilter]);

  const recruiterOptions = useMemo(() => {
    const map = new Map();
    rowsForRecruiterList.forEach((r) => {
      const value = recruiterKey(r);
      if (!value) return;
      const label = r.user_name?.trim() || value;
      if (!map.has(value)) map.set(value, label);
    });
    return [...map.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: "base" }))
      .map(([value, label]) => ({ value, label }));
  }, [rowsForRecruiterList]);

  useEffect(() => {
    if (!recruiterFilter) return;
    const stillValid = recruiterOptions.some((o) => o.value === recruiterFilter);
    if (!stillValid) onRecruiterFilterChange("");
  }, [recruiterOptions, recruiterFilter, onRecruiterFilterChange]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (recruiterFilter && recruiterKey(r) !== recruiterFilter) return false;
      if (managerFilter && managerKey(r) !== managerFilter) return false;
      return true;
    });
  }, [rows, recruiterFilter, managerFilter]);

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body" style={{ maxHeight: "35vh", overflowY: "auto" }}>
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
          <h5 className="mb-0">
            Recruitment Pipeline
            <HelpTooltip text="Counts are for the selected month. Click Submissions, L1, or Joined to open the related module." />
          </h5>
          <div className="d-flex flex-wrap gap-2 align-items-end" style={{ maxWidth: "100%" }}>
            <div style={{ minWidth: 200, maxWidth: 280 }}>
              <label className="form-label small text-muted mb-1">Manager</label>
              <select
                className="form-select form-select-sm"
                value={managerFilter}
                onChange={(e) => {
                  onManagerFilterChange(e.target.value);
                  onRecruiterFilterChange("");
                }}
                disabled={rows.length === 0}
              >
                <option value="">All managers</option>
                {managerOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 200, maxWidth: 280 }}>
              <label className="form-label small text-muted mb-1">Recruiter</label>
              <select
                className="form-select form-select-sm"
                value={recruiterFilter}
                onChange={(e) => onRecruiterFilterChange(e.target.value)}
                disabled={rows.length === 0}
              >
                <option value="">All recruiters</option>
                {recruiterOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <style>
            {`
    .stylishback tr th{
        background: var(--primary-border) !important;
        color: #fff !important;
        font-weight: bold !important;
    }
    .table{
        border-color: var(--primary-border) !important;
    }
`}
          </style>
          <table className="table table-bordered align-middle text-center table-sm">
            <thead
              className="stylishback"
              style={{ backgroundColor: "#0d6efd", color: "#fff", fontWeight: "bold" }}
            >
              <tr>
                <th>Recruiter</th>
                <th>Manager</th>
                <th>Demand Assigned</th>
                <th>→</th>
                <th>Profiles Submitted</th>
                <th>→</th>
                <th>L1</th>
                <th>→</th>
                <th>L2</th>
                <th>→</th>
                <th>L3</th>
                <th>→</th>
                <th>Final Select</th>
                <th>→</th>
                <th>Offer</th>
                <th>→</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={17} className="text-muted py-4">
                    {rows.length === 0
                      ? "No pipeline data for this month."
                      : "No rows match the selected filters."}
                  </td>
                </tr>
              ) : (
                filteredRows.map((user, idx) => (
                  <tr key={`${recruiterKey(user)}-${managerKey(user)}-${idx}`}>
                    <td className="fw-bold">{user.user_name}</td>
                    <td>{user.manager_name || "N/A"}</td>
                    <td>{user.total_positions}</td>
                    <td>→</td>
                    <td>{drillLink("/candidates", "Open candidates list")(user.total_submissions)}</td>
                    <td>→</td>
                    <td>{drillLink("/interviews", "Open interviews list")(user.total_l1_scheduled)}</td>
                    <td>→</td>
                    <td>{drillLink("/interviews", "Open interviews list")(user.total_l2_scheduled)}</td>
                    <td>→</td>
                    <td>{drillLink("/interviews", "Open interviews list")(user.total_l3_scheduled)}</td>
                    <td>→</td>
                    <td>{drillLink("/candidates", "Open candidates list")(user.total_hired)}</td>
                    <td>→</td>
                    <td>{drillLink("/onboards", "Open onboards list")(user.total_offer ?? 0)}</td>
                    <td>→</td>
                    <td>{drillLink("/onboards", "Open onboards list")(user.total_started)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
